/**
 * PilotLinkUSA – Unified Dashboard
 * Combines: CompanyDashboard + DriverDashboard + DispatcherConsole
 *
 * Usage: Replace your three separate dashboard components with this one.
 * Role is auto-detected from localStorage user.role:
 *   "company"    → Company portal (post loads, manage postings)
 *   "driver"     → Driver portal  (find loads, training hub)
 *   "dispatcher" → Dispatcher console (live GPS, driver chat, alerts)
 *
 * Dependencies: react, react-dom, react-router-dom, lucide-react
 * Hooks: useGPS.js, useChat.js, api.js  (from pilotlink_dispatcher project)
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import {
  Truck, DollarSign, Plus, MapPin, Activity, Loader2,
  ShieldAlert, Clock, CheckCircle2, ArrowRight, Map,
  FileCheck, Award, BookOpen, ChevronRight, ChevronDown, ChevronUp,
  Bell, Search, Menu, X, Home, Package, Users, BarChart2,
  Settings, LogOut, Star, RefreshCw, Eye, TrendingUp, Zap,
  Satellite, MessageCircle, AlertTriangle, Info, Send,
  Pause, Play, RotateCcw, Navigation,
} from "lucide-react";

// ─── Config ──────────────────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
const WS_BASE = process.env.REACT_APP_WS_URL  || "ws://127.0.0.1:8000";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt    = (n)   => parseFloat(n || 0).toFixed(2);
const fmtD   = (d, o = { weekday:"short", month:"short", day:"numeric" }) =>
  d ? new Date(d).toLocaleDateString("en-US", o) : "—";
const greet  = ()    => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; };
const tnow   = ()    => new Date().toLocaleTimeString("en-US", { hour:"2-digit", minute:"2-digit", hour12:false });

// ─── Reusable primitives ──────────────────────────────────────────────────────
function Badge({ children, color = "slate" }) {
  const map = {
    cyan:    "bg-cyan-100 text-cyan-800 dark:bg-cyan-500/20 dark:text-cyan-300",
    amber:   "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300",
    emerald: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300",
    indigo:  "bg-indigo-100 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-300",
    red:     "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300",
    slate:   "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-slate-300",
    blue:    "bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${map[color]}`}>
      {children}
    </span>
  );
}

function Btn({ children, onClick, variant = "primary", size = "md", className = "", disabled = false, to }) {
  const base = "inline-flex items-center justify-center gap-1.5 font-semibold rounded-xl transition-all active:scale-95 disabled:opacity-50 disabled:pointer-events-none";
  const sizes = { xs:"px-2.5 py-1 text-[10px]", sm:"px-3 py-1.5 text-xs", md:"px-4 py-2 text-sm", lg:"px-5 py-2.5 text-sm" };
  const variants = {
    primary:    "bg-cyan-600 hover:bg-cyan-700 text-white shadow-sm",
    secondary:  "bg-slate-900 hover:bg-slate-800 text-white dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100",
    outline:    "border border-slate-200 hover:bg-slate-50 text-slate-700 dark:border-white/10 dark:text-white dark:hover:bg-white/5",
    ghost:      "hover:bg-slate-100 text-slate-600 dark:hover:bg-white/5 dark:text-slate-300",
    indigo:     "bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm",
    navy:       "bg-slate-900 hover:bg-slate-800 text-white shadow-sm",
    danger:     "bg-red-500 hover:bg-red-600 text-white",
  };
  const cls = `${base} ${sizes[size]} ${variants[variant]} ${className}`;
  if (to) return <Link to={to} className={cls}>{children}</Link>;
  return <button onClick={onClick} className={cls} disabled={disabled}>{children}</button>;
}

function StatCard({ label, value, sub, icon: Icon, iconBg, iconColor, trend, trendUp }) {
  return (
    <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-2.5">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${iconBg}`}>
          <Icon className={`w-4.5 h-4.5 ${iconColor}`} />
        </div>
        {trend != null && (
          <span className={`text-[10px] font-semibold flex items-center gap-0.5 ${trendUp !== false ? "text-emerald-600" : "text-red-500"}`}>
            <TrendingUp className="w-3 h-3" /> {trend}
          </span>
        )}
      </div>
      <p className="text-[10px] font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-2xl font-bold text-slate-900 dark:text-white leading-none">{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

// ─── Load Detail Modal (shared across all roles) ───────────────────────────────
function LoadModal({ load, onClose, isDriver = false }) {
  if (!load) return null;
  const hasEscort = load.needs_front_escort || load.needs_chase_escort || load.needs_height_pole || load.needs_steersman;
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white dark:bg-slate-900 shadow-2xl ring-1 ring-slate-200 dark:ring-white/10 animate-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between border-b border-slate-100 dark:border-white/5 px-5 py-4">
          <div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white">Load Details</h3>
            <p className="text-[10px] text-slate-400 mt-0.5">#{load.id}</p>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {/* Route */}
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-2">Route</p>
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0"><p className="text-[10px] text-slate-400">From</p><p className="text-sm font-bold text-slate-900 dark:text-white truncate">{load.origin}</p></div>
              <ArrowRight className="w-4 h-4 text-cyan-500 flex-shrink-0" strokeWidth={2.5} />
              <div className="flex-1 min-w-0 text-right"><p className="text-[10px] text-slate-400">To</p><p className="text-sm font-bold text-slate-900 dark:text-white truncate">{load.destination}</p></div>
            </div>
            <p className="text-xs text-slate-400 mt-2">📅 {fmtD(load.departure_date, { weekday:"long", year:"numeric", month:"long", day:"numeric" })}</p>
          </div>
          {/* Grid */}
          <div className="grid grid-cols-2 gap-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
            {[
              { label:"Commodity",   value: load.commodity },
              { label:"Weight",      value: load.weight || "N/A" },
              { label:"Dimensions",  value: `${load.width||"—"} × ${load.height||"—"} × ${load.length||"—"}` },
              { label: isDriver ? "Driver Payout" : "Fee Paid", value: `$${fmt(load.posting_fee)}`, green: true },
            ].map(({ label, value, green }) => (
              <div key={label}>
                <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1">{label}</p>
                <p className={`text-xs font-semibold ${green ? "text-emerald-600 dark:text-emerald-400" : "text-slate-900 dark:text-white"}`}>{value}</p>
              </div>
            ))}
          </div>
          {/* Escort */}
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-2">Escort Requirements</p>
            <div className="flex flex-wrap gap-1.5">
              {load.needs_front_escort && <Badge color="cyan">Front Escort</Badge>}
              {load.needs_chase_escort && <Badge color="cyan">Chase Escort</Badge>}
              {load.needs_height_pole  && <Badge color="amber">Height Pole</Badge>}
              {load.needs_steersman   && <Badge color="indigo">Steersman</Badge>}
              {!hasEscort && <span className="text-xs italic text-slate-400">Standard escort only</span>}
            </div>
          </div>
        </div>
        <div className="border-t border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/20 px-5 py-3 flex items-center justify-between">
          <Btn variant="outline" size="sm" onClick={onClose}>Close</Btn>
          <Btn variant={isDriver ? "primary" : "secondary"} size="sm">
            {isDriver ? "Accept Load" : load.status?.toLowerCase() === "open" ? "View Status" : "View Driver Info"}
          </Btn>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── GPS WebSocket hook ───────────────────────────────────────────────────────
function useGPS(token) {
  const [drivers, setDrivers] = useState(new Map());
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const timerRef = useRef(null);

  const connect = useCallback(() => {
    if (!token) return;
    try {
      const ws = new WebSocket(`${WS_BASE}/ws/gps/?token=${token}`);
      wsRef.current = ws;
      ws.onopen  = () => { setConnected(true); ws.send(JSON.stringify({ type:"gps.subscribe" })); timerRef.current = setInterval(() => ws.readyState === 1 && ws.send(JSON.stringify({ type:"ping" })), 25000); };
      ws.onclose = () => { setConnected(false); clearInterval(timerRef.current); setTimeout(connect, 3000); };
      ws.onerror = () => ws.close();
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "driver.position") setDrivers(p => { const n = new Map(p); n.set(String(msg.driver_id), msg); return n; });
          else if (msg.type === "all.positions") { const m = new Map(); msg.drivers.forEach(d => m.set(String(d.driver_id), d)); setDrivers(m); }
        } catch (_) {}
      };
    } catch (_) { setConnected(false); }
  }, [token]);

  useEffect(() => { connect(); return () => { clearInterval(timerRef.current); wsRef.current?.close(); }; }, [connect]);
  return { drivers, connected };
}

// ─── Chat WebSocket hook ──────────────────────────────────────────────────────
function useChat(token, roomName) {
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    if (!token || !roomName) return;
    try {
      const ws = new WebSocket(`${WS_BASE}/ws/chat/${roomName}/?token=${token}`);
      wsRef.current = ws;
      ws.onopen  = () => setConnected(true);
      ws.onclose = () => setConnected(false);
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "chat.history") setMessages(msg.messages);
          else if (msg.type === "chat.message") setMessages(p => [...p, msg]);
        } catch (_) {}
      };
    } catch (_) {}
    return () => wsRef.current?.close();
  }, [token, roomName]);

  const send = useCallback((text) => {
    if (wsRef.current?.readyState === 1 && text.trim())
      wsRef.current.send(JSON.stringify({ type:"chat.message", text }));
  }, []);

  return { messages, send, connected };
}

// ─── GPS SVG Map ─────────────────────────────────────────────────────────────
const GPS_ROUTE = [
  {lat:35.37,lng:-119.02},{lat:35.1,lng:-118.4},{lat:34.8,lng:-117.9},
  {lat:34.5,lng:-116.9},{lat:34.2,lng:-115.5},{lat:33.8,lng:-114.2},
  {lat:33.5,lng:-113.0},{lat:33.45,lng:-112.07},
];
const STEPS_TOTAL = (GPS_ROUTE.length - 1) * 20;
const lerpN = (a, b, t) => a + (b - a) * t;

function LiveGPSMap({ drivers, selectedDriverId, onSelectDriver, simIdx, simProg, simRunning }) {
  const W = 600, H = 280;
  const minLat=33.0,maxLat=36.5,minLng=-120.5,maxLng=-111.0;
  const tx = (lng) => ((lng - minLng) / (maxLng - minLng)) * W;
  const ty = (lat) => (1 - (lat - minLat) / (maxLat - minLat)) * H;
  const c = GPS_ROUTE[Math.min(simIdx, GPS_ROUTE.length-1)];
  const n = GPS_ROUTE[Math.min(simIdx+1, GPS_ROUTE.length-1)];
  const cLat = lerpN(c.lat, n.lat, simProg);
  const cLng = lerpN(c.lng, n.lng, simProg);
  const routePts = GPS_ROUTE.map(p => `${tx(p.lng).toFixed(1)},${ty(p.lat).toFixed(1)}`).join(" ");
  const done = Math.min(simIdx*20+Math.round(simProg*20), STEPS_TOTAL);
  const di = Math.floor(done/20), dp = (done%20)/20;
  const donePts = GPS_ROUTE.slice(0, di+1).map(p => `${tx(p.lng).toFixed(1)},${ty(p.lat).toFixed(1)}`);
  if (di < GPS_ROUTE.length-1) { const cc=GPS_ROUTE[di],nn=GPS_ROUTE[di+1]; donePts.push(`${tx(lerpN(cc.lng,nn.lng,dp)).toFixed(1)},${ty(lerpN(cc.lat,nn.lat,dp)).toFixed(1)}`); }
  const mx = parseFloat(tx(cLng).toFixed(1)), my = parseFloat(ty(cLat).toFixed(1));
  const driverList = Array.from(drivers.values());

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full cursor-pointer">
      <rect width={W} height={H} fill="#e8efe8"/>
      {[.3,.6].map(f=><line key={`h${f}`} x1={0} y1={H*f} x2={W} y2={H*f} stroke="#cdd9b8" strokeWidth="1"/>)}
      {[.2,.4,.6,.8].map(f=><line key={`v${f}`} x1={W*f} y1={0} x2={W*f} y2={H} stroke="#cdd9b8" strokeWidth="1"/>)}
      <ellipse cx={130} cy={60} rx={60} ry={22} fill="#c5d5a8" opacity=".45"/>
      <ellipse cx={400} cy={170} rx={75} ry={28} fill="#cddaae" opacity=".4"/>
      {[{t:"California",x:80,y:140},{t:"Arizona",x:410,y:190},{t:"Nevada",x:290,y:60}].map(l=>(
        <text key={l.t} x={l.x} y={l.y} fontSize="9" fill="#6b7280" fontFamily="system-ui" textAnchor="middle">{l.t}</text>
      ))}
      <polyline points={routePts} fill="none" stroke="#16a34a" strokeWidth="2" strokeDasharray="5 3" opacity=".5"/>
      {donePts.length > 1 && <polyline points={donePts.join(" ")} fill="none" stroke="#2563eb" strokeWidth="3"/>}
      {GPS_ROUTE.map((p,i)=><circle key={i} cx={tx(p.lng).toFixed(1)} cy={ty(p.lat).toFixed(1)} r="3" fill={i===0?"#10b981":i===GPS_ROUTE.length-1?"#ef4444":"#9ca3af"} stroke="#fff" strokeWidth="1.5"/>)}
      {/* Simulated moving marker */}
      <circle cx={mx} cy={my} r="10" fill="#ea580c" stroke="#fff" strokeWidth="2"/>
      <text x={mx} y={my+4} textAnchor="middle" fontSize="8" fill="#fff" fontFamily="system-ui" fontWeight="600">MR</text>
      {simRunning && (
        <circle cx={mx} cy={my} r="14" fill="none" stroke="#ea580c" strokeWidth="1.5" opacity=".4">
          <animate attributeName="r" values="10;22;10" dur="1.8s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values=".5;0;.5" dur="1.8s" repeatCount="indefinite"/>
        </circle>
      )}
      {/* Real WebSocket driver markers */}
      {driverList.map(d => {
        if (!d.lat || !d.lng) return null;
        const x = tx(d.lng), y = ty(d.lat);
        const isSel = String(d.driver_id) === String(selectedDriverId);
        return (
          <g key={d.driver_id} onClick={() => onSelectDriver(d)} style={{cursor:"pointer"}}>
            {isSel && <circle cx={x} cy={y} r="16" fill="none" stroke="#2563eb" strokeWidth="2"/>}
            <circle cx={x} cy={y} r="10" fill={d.status==="active"?"#16a34a":d.status==="on_break"?"#d97706":"#6b7280"} stroke="#fff" strokeWidth="2"/>
            <text x={x} y={y+4} textAnchor="middle" fontSize="8" fill="#fff" fontFamily="system-ui" fontWeight="600">{d.avatar}</text>
          </g>
        );
      })}
      <rect x={W-38} y={8} width="28" height="22" rx="4" fill="#fff" stroke="#e2e8f0" strokeWidth="1"/>
      <text x={W-24} y={23} textAnchor="middle" fontSize="15" fill="#374151">+</text>
      <rect x={W-38} y={34} width="28" height="22" rx="4" fill="#fff" stroke="#e2e8f0" strokeWidth="1"/>
      <text x={W-24} y={49} textAnchor="middle" fontSize="15" fill="#374151">−</text>
    </svg>
  );
}

// ─── Dispatcher Chat Panel ────────────────────────────────────────────────────
function DispatcherChat({ token, driver, currentUserId }) {
  const roomName = driver ? `driver_${driver.id || driver.driver_id}` : "broadcast";
  const { messages, send, connected } = useChat(token, roomName);
  const [inp, setInp] = useState("");
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages]);
  const handleSend = () => { if (inp.trim()) { send(inp.trim()); setInp(""); } };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2.5 px-3 py-2.5 border-b border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/30 flex-shrink-0">
        <div className="w-8 h-8 rounded-lg bg-cyan-100 dark:bg-cyan-500/20 flex items-center justify-center font-bold text-cyan-700 dark:text-cyan-300 text-xs flex-shrink-0">
          {driver?.avatar || "ALL"}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-slate-800 dark:text-white truncate">{driver?.driver_name || "Broadcast"}</p>
          <div className="flex items-center gap-1.5">
            <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-green-500" : "bg-slate-300"}`}/>
            <span className={`text-[10px] ${connected ? "text-green-600" : "text-slate-400"}`}>{connected ? "Connected" : "Reconnecting…"}</span>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-2.5 space-y-2.5 bg-white dark:bg-slate-900">
        {messages.length === 0 && <p className="text-[10px] text-slate-300 text-center mt-6">No messages. Start the conversation.</p>}
        {messages.map((m) => {
          const isMe = m.role === "dispatcher" || m.sender_id === currentUserId;
          return (
            <div key={m.id || m.timestamp} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
              <div className={`flex flex-col ${isMe ? "items-end" : "items-start"} max-w-[80%]`}>
                {!isMe && <span className="text-[9px] text-slate-400 mb-0.5 px-1">{m.sender_name || m.avatar}</span>}
                <div className={`px-2.5 py-1.5 rounded-2xl text-xs leading-relaxed ${isMe ? "bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-white rounded-br-sm" : "bg-cyan-600 text-white rounded-bl-sm"}`}>
                  {m.text || m.t}
                </div>
                <span className="text-[9px] text-slate-300 mt-0.5 px-1">{m.timestamp ? new Date(m.timestamp).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}) : m.ts}</span>
              </div>
            </div>
          );
        })}
        <div ref={endRef}/>
      </div>
      <div className="px-2.5 py-2.5 border-t border-slate-100 dark:border-white/5 flex-shrink-0 bg-white dark:bg-slate-900">
        <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-white/10 rounded-xl px-2.5 py-1.5">
          <input
            value={inp} onChange={e=>setInp(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleSend()}
            placeholder={`Message ${driver?.driver_name?.split(" ")[0] || "all"}…`}
            className="flex-1 bg-transparent text-xs text-slate-800 dark:text-white placeholder-slate-400 outline-none"
          />
          <button onClick={handleSend} disabled={!inp.trim()||!connected} className="flex-shrink-0 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 text-white text-[10px] font-bold px-2 py-1 rounded-lg transition-colors">
            <Send className="w-3 h-3"/>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Company View ─────────────────────────────────────────────────────────────
function CompanyView({ token }) {
  const user = (() => { try { return JSON.parse(localStorage.getItem("user")); } catch { return null; } })();
  const [loads, setLoads]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selectedLoad, setLoad]   = useState(null);
  const [filter, setFilter]       = useState("all");

  useEffect(() => {
    if (!user?.email) { setLoading(false); return; }
    fetch(`${API_URL}/api/loads/my-loads/?email=${user.email}`)
      .then(r=>r.json()).then(d=>setLoads(d.results||[])).catch(console.warn).finally(()=>setLoading(false));
  }, [user?.email]);

  const open     = loads.filter(l=>l.status?.toLowerCase()==="open").length;
  const assigned = loads.filter(l=>l.status?.toLowerCase()!=="open").length;
  const total    = loads.reduce((a,l)=>a+parseFloat(l.posting_fee||0),0);
  const filtered = filter==="all"?loads:filter==="open"?loads.filter(l=>l.status?.toLowerCase()==="open"):loads.filter(l=>l.status?.toLowerCase()!=="open");

  return (
    <div className="space-y-5">
      {/* Welcome */}
      <div className="rounded-2xl bg-indigo-600 p-4 flex items-center justify-between gap-4">
        <div><h2 className="text-white font-bold text-sm">{greet()}, team! 👋</h2><p className="text-indigo-200 text-xs mt-1">{open} load{open!==1?"s":""} awaiting a driver match.</p></div>
        <Btn variant="outline" size="sm" to="/post-load" className="bg-white/20 border-white/30 text-white hover:bg-white/30 flex-shrink-0"><Plus className="w-3.5 h-3.5"/> Post load</Btn>
      </div>
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Active Postings"  value={open}     sub="Awaiting match" icon={Activity}    iconBg="bg-indigo-50 dark:bg-indigo-500/10" iconColor="text-indigo-600 dark:text-indigo-400" trend="↑ 12%" />
        <StatCard label="Assigned Drivers" value={assigned} sub="All time"       icon={Truck}       iconBg="bg-emerald-50 dark:bg-emerald-500/10" iconColor="text-emerald-600 dark:text-emerald-400" trend="↑ 5%" />
        <StatCard label="Marketplace Fees" value={`$${total.toFixed(0)}`} sub="Total spent" icon={DollarSign} iconBg="bg-purple-50 dark:bg-purple-500/10" iconColor="text-purple-600 dark:text-purple-400" />
      </div>
      {/* Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-900 dark:text-white">Your Recent Postings</h2>
            <div className="flex gap-1.5">
              {["all","open","assigned"].map(f=>(
                <button key={f} onClick={()=>setFilter(f)} className={`px-2.5 py-1 rounded-full text-[10px] font-semibold capitalize transition-colors ${filter===f?"bg-indigo-600 text-white":"bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400 hover:bg-slate-200"}`}>{f}</button>
              ))}
            </div>
          </div>
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-indigo-500"/></div>
          ) : filtered.length===0 ? (
            <div className="flex flex-col items-center py-14 border-2 border-dashed border-slate-200 dark:border-white/10 rounded-2xl text-center">
              <MapPin className="w-9 h-9 text-slate-300 mb-3"/>
              <p className="font-bold text-slate-800 dark:text-white text-sm mb-1">No Loads Found</p>
              <p className="text-xs text-slate-400 mb-4 max-w-xs">{loads.length===0?"Post your first oversize load to get started.":"No loads match this filter."}</p>
              <Btn variant="primary" to="/post-load" size="sm"><Plus className="w-3.5 h-3.5"/> Post a Load</Btn>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map(load=>{
                const isOpen=load.status?.toLowerCase()==="open";
                return (
                  <div key={load.id} className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer" onClick={()=>setLoad(load)}>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          <Badge color={isOpen?"amber":"emerald"}>{isOpen?<Clock className="w-2.5 h-2.5"/>:<CheckCircle2 className="w-2.5 h-2.5"/>}{isOpen?"Pending Match":"Assigned"}</Badge>
                          {load.is_featured&&<Badge color="indigo"><Star className="w-2.5 h-2.5"/> Featured</Badge>}
                        </div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-bold text-slate-900 dark:text-white truncate">{load.origin}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" strokeWidth={2.5}/>
                          <span className="text-sm font-bold text-slate-900 dark:text-white truncate">{load.destination}</span>
                        </div>
                        <p className="text-[10px] text-slate-400"><Truck className="w-3 h-3 inline mr-1"/>{load.commodity} • {fmtD(load.departure_date)}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {load.needs_front_escort&&<Badge>Front Escort</Badge>}
                          {load.needs_chase_escort&&<Badge>Chase Escort</Badge>}
                          {load.needs_height_pole&&<Badge color="amber">Height Pole</Badge>}
                        </div>
                      </div>
                      <div className="flex sm:flex-col items-center sm:items-end gap-3 pt-3 sm:pt-0 border-t sm:border-t-0 sm:border-l border-slate-100 dark:border-white/5 sm:pl-4">
                        <div className="text-right"><p className="text-[9px] text-slate-400 font-medium uppercase tracking-wider">Fee</p><p className="text-base font-bold text-slate-900 dark:text-white">${fmt(load.posting_fee)}</p></div>
                        <Btn variant={isOpen?"outline":"secondary"} size="sm" onClick={e=>{e.stopPropagation();setLoad(load);}}><Eye className="w-3.5 h-3.5"/>{isOpen?"View Status":"Driver Info"}</Btn>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="space-y-4">
          <div className="relative overflow-hidden bg-gradient-to-br from-cyan-50 to-white dark:from-cyan-950/40 dark:to-slate-900 border border-slate-200 dark:border-white/10 rounded-2xl p-5 text-center">
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-cyan-400/10 blur-xl"/>
            <div className="relative">
              <div className="w-12 h-12 rounded-2xl bg-white dark:bg-slate-800 shadow-md ring-1 ring-slate-200/50 dark:ring-white/10 flex items-center justify-center mx-auto mb-3"><MapPin className="w-6 h-6 text-cyan-600 dark:text-cyan-400"/></div>
              <h3 className="font-bold text-slate-900 dark:text-white text-sm mb-1">Dispatch a Load</h3>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 mb-4 leading-relaxed">Broadcast your oversize route to our nationwide network of certified pilot car professionals.</p>
              <Btn variant="primary" to="/post-load" className="w-full justify-center"><Plus className="w-3.5 h-3.5"/> Post New Load</Btn>
            </div>
          </div>
          <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-4">
            <p className="text-xs font-bold text-slate-800 dark:text-white mb-3">Load Summary</p>
            {[["Total Posted",loads.length],["Pending Match",open],["Assigned",assigned],["Total Fees",`$${total.toFixed(2)}`]].map(([l,v])=>(
              <div key={l} className="flex justify-between py-1.5 border-b border-slate-50 dark:border-white/5 last:border-0">
                <span className="text-[10px] text-slate-400">{l}</span>
                <span className="text-[10px] font-bold text-slate-800 dark:text-white">{v}</span>
              </div>
            ))}
          </div>
          <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-4">
            <div className="flex items-start gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center flex-shrink-0"><ShieldAlert className="w-4 h-4 text-amber-600 dark:text-amber-400"/></div>
              <div><p className="text-xs font-bold text-slate-900 dark:text-white">Need Support?</p><p className="text-[10px] text-slate-400 mt-1 mb-2 leading-relaxed">Questions about compliance or dispatching? Our team is available 24/7.</p><span className="text-[10px] font-bold text-cyan-600 dark:text-cyan-400 cursor-pointer hover:underline">Contact Dispatch Team →</span></div>
            </div>
          </div>
        </div>
      </div>
      {selectedLoad && <LoadModal load={selectedLoad} onClose={()=>setLoad(null)} isDriver={false}/>}
    </div>
  );
}

// ─── Driver View ──────────────────────────────────────────────────────────────
function DriverView({ token }) {
  const navigate = useNavigate();
  const user = (() => { try { return JSON.parse(localStorage.getItem("user")); } catch { return null; } })();
  const [loads,          setLoads]          = useState([]);
  const [loadsLoading,   setLoadsLoading]   = useState(true);
  const [selectedLoad,   setLoad]           = useState(null);
  const [trainingData,   setTraining]       = useState(null);
  const [allEnrollments, setEnrollments]    = useState([]);
  const [trainingLoading,setTrLoading]      = useState(true);
  const [expanded,       setExpanded]       = useState(false);
  const [filter,         setFilter]         = useState("all");

  useEffect(() => {
    fetch(`${API_URL}/api/loads/available/`).then(r=>r.json()).then(d=>setLoads(d.results||d||[])).catch(console.warn).finally(()=>setLoadsLoading(false));
  }, []);

  useEffect(() => {
    if (!user?.email) { setTrLoading(false); return; }
    const go = async () => {
      try {
        const er = await fetch(`${API_URL}/api/enrollments/active/?email=${encodeURIComponent(user.email)}`);
        const ed = await er.json();
        if (ed.enrollments) setEnrollments(ed.enrollments);
        const code = ed.active_course_code || "WA_PEVO_PREP";
        const tr = await fetch(`${API_URL}/api/course-content/${code}/?email=${encodeURIComponent(user.email)}`);
        if (tr.ok) { const td=await tr.json(); td.course_code=code; setTraining(td); }
      } catch(e){console.warn(e);} finally{setTrLoading(false);}
    };
    go();
  }, [user?.email]);

  let prog=0,prevMod=null,curMod=null,complete=false;
  if (trainingData?.is_enrolled && trainingData.modules) {
    const cids=trainingData.completed_items||[];
    let total=0,doneIdx=0;
    trainingData.modules.forEach((m,i)=>{const items=[...(m.videos||[]),...(m.documents||[])];total+=items.length;if(items.length>0&&items.every(it=>cids.includes(it.id)))doneIdx=i+1;});
    if(total>0)prog=Math.round((cids.length/total)*100);
    if(doneIdx>=trainingData.modules.length){complete=true;prevMod=`Module ${trainingData.modules.length}`;}
    else{curMod=trainingData.modules[doneIdx];if(doneIdx>0)prevMod=`Module ${doneIdx}`;}
  }

  const filtered = filter==="all"?loads:filter==="escort"?loads.filter(l=>l.needs_front_escort||l.needs_chase_escort):loads.filter(l=>l.is_featured);

  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-cyan-600 p-4 flex items-center justify-between gap-4">
        <div><h2 className="text-white font-bold text-sm">{greet()}, {user?.first_name||"Driver"}! 👋</h2><p className="text-cyan-100 text-xs mt-1">{loads.length} loads available near you today.</p></div>
        <Btn variant="outline" size="sm" onClick={()=>navigate("/course-catlog-details")} className="bg-white/20 border-white/30 text-white hover:bg-white/30 flex-shrink-0"><BookOpen className="w-3.5 h-3.5"/> Training Hub</Btn>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Active Certs"     value="2 / 50"  sub="States certified" icon={FileCheck} iconBg="bg-emerald-50 dark:bg-emerald-500/10" iconColor="text-emerald-600 dark:text-emerald-400"/>
        <StatCard label="Available Loads"  value={loads.length} sub="Near you"   icon={Map}       iconBg="bg-cyan-50 dark:bg-cyan-500/10"    iconColor="text-cyan-600 dark:text-cyan-400" trend="↑ 8%"/>
        <StatCard label="Training"         value={`${trainingLoading?"—":`${trainingData?.is_enrolled?prog:0}%`}`} sub={trainingData?.is_enrolled?"Active cert":"Not enrolled"} icon={BookOpen} iconBg="bg-indigo-50 dark:bg-indigo-500/10" iconColor="text-indigo-600 dark:text-indigo-400"/>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-900 dark:text-white">Recommended Loads</h2>
            <div className="flex gap-1.5">
              {["all","escort","featured"].map(f=>(
                <button key={f} onClick={()=>setFilter(f)} className={`px-2.5 py-1 rounded-full text-[10px] font-semibold capitalize transition-colors ${filter===f?"bg-cyan-600 text-white":"bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400 hover:bg-slate-200"}`}>{f}</button>
              ))}
            </div>
          </div>
          {loadsLoading?<div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-cyan-500"/></div>:
          filtered.length===0?<div className="text-center py-14 border-2 border-dashed border-slate-200 dark:border-white/10 rounded-2xl"><Truck className="w-8 h-8 text-slate-300 mx-auto mb-3"/><p className="text-xs text-slate-400">No loads match this filter.</p></div>:
          <div className="space-y-3">
            {filtered.map(load=>(
              <div key={load.id} className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer" onClick={()=>setLoad(load)}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {load.needs_front_escort&&<Badge color="cyan">Front Escort</Badge>}
                      {load.needs_chase_escort&&<Badge>Chase Escort</Badge>}
                      {load.needs_height_pole&&<Badge color="amber">Height Pole</Badge>}
                      {load.is_featured&&<Badge color="indigo"><Star className="w-2.5 h-2.5"/> Featured</Badge>}
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-bold text-slate-900 dark:text-white truncate">{load.origin}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" strokeWidth={2.5}/>
                      <span className="text-sm font-bold text-slate-900 dark:text-white truncate">{load.destination}</span>
                    </div>
                    <p className="text-[10px] text-slate-400">{fmtD(load.departure_date)} • {load.commodity}</p>
                  </div>
                  <div className="flex sm:flex-col items-center sm:items-end gap-3 pt-3 sm:pt-0 border-t sm:border-t-0 sm:border-l border-slate-100 dark:border-white/5 sm:pl-4">
                    <p className="text-base font-bold text-emerald-600 dark:text-emerald-400">${fmt(load.posting_fee)}</p>
                    <Btn variant="primary" size="sm" onClick={e=>{e.stopPropagation();setLoad(load);}}>View Details</Btn>
                  </div>
                </div>
              </div>
            ))}
          </div>}
        </div>
        <div className="space-y-4">
          <h2 className="text-sm font-bold text-slate-900 dark:text-white">Training Hub</h2>
          <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-4">
            {trainingLoading?<div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-indigo-500"/></div>:
            trainingData?.is_enrolled?(
              <div>
                <div className="flex items-start gap-2.5 mb-4">
                  <div className="w-8 h-8 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center flex-shrink-0"><BookOpen className="w-4 h-4 text-indigo-600 dark:text-indigo-400"/></div>
                  <div className="flex-1 min-w-0"><p className="text-xs font-bold text-slate-900 dark:text-white">Resume Certification</p><p className="text-[10px] text-slate-400 truncate mt-0.5">{trainingData.course_title}</p></div>
                </div>
                <div className="mb-3"><div className="flex justify-between text-[10px] font-medium text-slate-400 mb-1.5"><span>Progress</span><span className="font-bold text-indigo-600 dark:text-indigo-400">{prog}%</span></div><div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-indigo-600 rounded-full transition-all duration-700" style={{width:`${prog}%`}}/></div></div>
                <div className="space-y-2 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-2.5 mb-3">
                  {prevMod&&<div className="flex items-center justify-between text-[10px]"><span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="w-3 h-3"/> {prevMod}</span><Badge color="emerald">Done</Badge></div>}
                  {complete?<div className="flex items-center justify-between text-[10px]"><span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 font-semibold"><Award className="w-3 h-3"/> All Modules Complete</span><span className="font-bold text-emerald-600">100%</span></div>:
                  curMod&&<div className="flex items-center justify-between text-[10px]"><span className="flex items-center gap-1.5 font-semibold text-slate-700 dark:text-slate-200"><span className="relative flex w-2 h-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"/><span className="relative inline-flex rounded-full w-2 h-2 bg-indigo-500"/></span>{curMod.title}</span><Badge color="indigo">Active</Badge></div>}
                </div>
                <Btn variant="indigo" to={`/course-details/${trainingData.course_code||"WA_PEVO_PREP"}`} className="w-full justify-center">{complete?"Review Material":"Continue Learning"}<ChevronRight className="w-3.5 h-3.5"/></Btn>
                {allEnrollments.length>1&&(
                  <div className="mt-3 border-t border-slate-100 dark:border-white/10 pt-3">
                    <button onClick={()=>setExpanded(e=>!e)} className="flex items-center justify-between w-full text-[10px] font-semibold text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                      <span>All active courses ({allEnrollments.length})</span>
                      {expanded?<ChevronUp className="w-3.5 h-3.5"/>:<ChevronDown className="w-3.5 h-3.5"/>}
                    </button>
                    {expanded&&<div className="mt-2 space-y-2">{allEnrollments.filter(c=>c.course_code!==trainingData.course_code).map(c=>(
                      <div key={c.course_code} className="flex items-center justify-between gap-2 p-2 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-white/5">
                        <span className="text-[10px] font-medium text-slate-700 dark:text-slate-200 truncate">{c.course_title}</span>
                        <Btn variant="outline" size="xs" to={`/course-details/${c.course_code}`}>Resume</Btn>
                      </div>
                    ))}</div>}
                  </div>
                )}
              </div>
            ):(
              <div>
                <div className="w-9 h-9 rounded-xl bg-slate-100 dark:bg-white/5 flex items-center justify-center mb-3"><BookOpen className="w-4.5 h-4.5 text-slate-400"/></div>
                <p className="text-xs font-bold text-slate-900 dark:text-white mb-1">No Active Training</p>
                <p className="text-[10px] text-slate-400 mb-3">You are not currently enrolled in any courses.</p>
                <Btn variant="outline" onClick={()=>navigate("/course-catlog-details")} className="w-full justify-center" size="sm">View Course Catalog</Btn>
              </div>
            )}
          </div>
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/40 dark:to-slate-900/40 border border-slate-200 dark:border-white/10 rounded-2xl p-4">
            <div className="w-8 h-8 rounded-xl bg-white dark:bg-slate-700 shadow-sm flex items-center justify-center mb-3"><Award className="w-4 h-4 text-amber-500"/></div>
            <p className="text-xs font-bold text-slate-900 dark:text-white mb-1">Add a State Cert</p>
            <p className="text-[10px] text-slate-400 leading-relaxed mb-3">Unlock more loads by getting certified in neighboring states.</p>
            <Btn variant="outline" to="/course-catlog-details" className="w-full justify-center bg-white dark:bg-transparent" size="sm">Browse Course Catalog</Btn>
          </div>
        </div>
      </div>
      {selectedLoad&&<LoadModal load={selectedLoad} onClose={()=>setLoad(null)} isDriver={true}/>}
    </div>
  );
}

// ─── Dispatcher View ──────────────────────────────────────────────────────────
function DispatcherView({ token, currentUserId }) {
  const { drivers, connected } = useGPS(token);
  const [selectedDriver, setSelDriver] = useState(null);
  const [rightTab, setRightTab]        = useState("chat"); // chat | drivers | alerts
  const [chatDriver, setChatDriver]    = useState(null);
  const [gpsModal, setGpsModal]        = useState(false);
  const [simRunning, setSimRunning]    = useState(false);
  const [simIdx, setSimIdx]            = useState(0);
  const [simProg, setSimProg]          = useState(0);
  const simRef = useRef(null);

  const driverList = Array.from(drivers.values());
  const stepsCompleted = Math.min(simIdx*20+Math.round(simProg*20), STEPS_TOTAL);
  const pct = Math.round((stepsCompleted/STEPS_TOTAL)*100);
  const etaMins = Math.max(0, Math.round(((STEPS_TOTAL-stepsCompleted)/STEPS_TOTAL)*320));

  useEffect(() => {
    if (simRunning) {
      simRef.current = setInterval(()=>{
        setSimProg(prev=>{
          if(prev>=1){setSimIdx(i=>{if(i>=GPS_ROUTE.length-2){setSimRunning(false);return i;}return i+1;});return 0;}
          return prev+0.05;
        });
      }, 350);
    } else clearInterval(simRef.current);
    return () => clearInterval(simRef.current);
  }, [simRunning]);

  const ALERTS = [
    {type:"warn",title:"Vehicle PLU-02-5678 delayed",sub:"Traffic jam on Highway 9",time:"10 min ago"},
    {type:"warn",title:"Maintenance due in 2 days",sub:"Vehicle PLU-03-9012",time:"30 min ago"},
    {type:"info",title:"New order assigned",sub:"PLU-2025-00130",time:"1 hr ago"},
  ];

  return (
    <div className="space-y-4">
      {/* Banner */}
      <div className="rounded-2xl p-4 flex items-center justify-between gap-4" style={{background:"#1a2744"}}>
        <div><h2 className="text-white font-bold text-sm">{greet()}, dispatch! 👋</h2><p className="text-blue-200 text-xs mt-1">{driverList.filter(d=>d.status==="active").length} drivers active via WebSocket.</p></div>
        <button onClick={()=>setGpsModal(true)} className="flex items-center gap-1.5 bg-white/15 border border-white/25 text-white text-xs font-semibold px-3 py-1.5 rounded-xl hover:bg-white/25 transition-colors flex-shrink-0"><Satellite className="w-3.5 h-3.5"/> GPS Recall</button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Active Drivers" value={`${driverList.filter(d=>d.status==="active").length} / ${Math.max(driverList.length,4)}`} sub="On route now" icon={Navigation} iconBg="bg-emerald-50 dark:bg-emerald-500/10" iconColor="text-emerald-600 dark:text-emerald-400" trend="↑ 2 today"/>
        <StatCard label="GPS Status"     value={connected?"Live":"Offline"} sub={connected?"WebSocket connected":"Reconnecting…"} icon={Satellite} iconBg={`${connected?"bg-cyan-50 dark:bg-cyan-500/10":"bg-red-50 dark:bg-red-500/10"}`} iconColor={`${connected?"text-cyan-600 dark:text-cyan-400":"text-red-600 dark:text-red-400"}`}/>
        <StatCard label="Alerts"         value={ALERTS.length} sub="Need attention" icon={Bell} iconBg="bg-amber-50 dark:bg-amber-500/10" iconColor="text-amber-600 dark:text-amber-400"/>
      </div>

      {/* Main 2-col */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* LEFT: Map + Driver list */}
        <div className="space-y-4">
          {/* Map */}
          <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-white/5">
              <div className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                Live Tracking
                {(simRunning||(connected&&driverList.length>0))&&<span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"/>}
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={()=>setSimRunning(r=>!r)} className={`text-[10px] px-2.5 py-1 rounded-lg font-semibold border transition-colors ${simRunning?"bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/30":"bg-slate-50 text-slate-600 border-slate-200 dark:bg-white/5 dark:border-white/10 hover:bg-slate-100"}`}>
                  {simRunning?<><Pause className="w-3 h-3 inline mr-1"/>Pause</>:<><Play className="w-3 h-3 inline mr-1"/>Start</>}
                </button>
                <button onClick={()=>{setSimRunning(false);setSimIdx(0);setSimProg(0);}} className="text-[10px] px-2 py-1 rounded-lg font-semibold bg-slate-50 text-slate-500 border border-slate-200 dark:bg-white/5 dark:border-white/10 hover:bg-slate-100">
                  <RotateCcw className="w-3 h-3"/>
                </button>
                <button onClick={()=>setGpsModal(true)} className="text-[10px] bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-2.5 py-1 rounded-lg font-semibold flex items-center gap-1">
                  <Satellite className="w-3 h-3"/> Recall
                </button>
              </div>
            </div>
            <div className="h-48 relative">
              <LiveGPSMap drivers={drivers} selectedDriverId={selectedDriver?.driver_id} onSelectDriver={d=>{setSelDriver(d);setChatDriver(d);}} simIdx={simIdx} simProg={simProg} simRunning={simRunning}/>
              {/* Overlay info */}
              {simRunning&&(
                <div className="absolute bottom-2 left-2 bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-white/10 rounded-xl px-2.5 py-2 text-[10px] shadow-sm space-y-0.5">
                  <div className="text-slate-400">Speed: <span className="font-bold text-amber-700">{Math.floor(52+Math.random()*20)} km/h</span></div>
                  <div className="text-slate-400">ETA: <span className="font-bold text-green-700">{etaMins>0?`${Math.floor(etaMins/60)}h ${etaMins%60}m`:"Arrived"}</span></div>
                  <div className="text-slate-400">Progress: <span className="font-bold text-cyan-700">{pct}%</span></div>
                </div>
              )}
            </div>
            <div className="px-4 py-2 border-t border-slate-100 dark:border-white/5">
              <div className="flex justify-between text-[9px] text-slate-400 mb-1"><span>Bakersfield, CA</span><span className="font-semibold text-slate-600 dark:text-white">{pct}%</span><span>Phoenix, AZ</span></div>
              <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all duration-300" style={{width:`${pct}%`,background:"linear-gradient(90deg,#3b82f6,#f59e0b)"}}/></div>
            </div>
          </div>

          {/* Drivers list */}
          <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-4">
            <p className="text-xs font-bold text-slate-900 dark:text-white mb-3">
              Drivers ({driverList.length > 0 ? driverList.length : "awaiting GPS…"})
            </p>
            {driverList.length === 0 ? (
              <div className="text-center py-6">
                <Satellite className="w-8 h-8 text-slate-300 mx-auto mb-2"/>
                <p className="text-[10px] text-slate-400">No drivers broadcasting GPS yet.</p>
                <p className="text-[10px] text-slate-300 mt-1">Drivers must open the Driver App and start tracking.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {driverList.map(d=>(
                  <div key={d.driver_id} onClick={()=>{setSelDriver(d);setChatDriver(d);setRightTab("chat");}} className={`flex items-center gap-2.5 p-2.5 rounded-xl border cursor-pointer transition-all ${selectedDriver?.driver_id===d.driver_id?"border-cyan-300 bg-cyan-50 dark:bg-cyan-500/10":"border-slate-100 dark:border-white/5 hover:border-cyan-200 dark:hover:border-white/10"}`}>
                    <div className="w-8 h-8 rounded-lg bg-cyan-100 dark:bg-cyan-500/20 flex items-center justify-center font-bold text-cyan-700 dark:text-cyan-300 text-xs flex-shrink-0">{d.avatar}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-800 dark:text-white truncate">{d.driver_name}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{d.vehicle} {d.speed>0?`• ${Math.round(d.speed)} km/h`:""}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${d.status==="active"?"bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300":d.status==="on_break"?"bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300":"bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-400"}`}>{d.status?.replace("_"," ")}</span>
                      <button onClick={e=>{e.stopPropagation();setChatDriver(d);setRightTab("chat");}} className="text-[9px] text-cyan-600 hover:text-cyan-800 font-semibold">Chat →</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Chat + Alerts */}
        <div className="space-y-4">
          {/* Tab bar */}
          <div className="flex bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden">
            {[["chat","Chat",MessageCircle],["drivers","All Drivers",Users],["alerts","Alerts",Bell]].map(([key,label,Icon])=>(
              <button key={key} onClick={()=>setRightTab(key)} className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold border-b-2 transition-colors ${rightTab===key?"border-cyan-600 text-cyan-700 dark:text-cyan-400":"border-transparent text-slate-400 hover:text-slate-700 dark:hover:text-white"}`}>
                <Icon className="w-3.5 h-3.5"/> {label}
              </button>
            ))}
          </div>

          {/* Chat tab */}
          {rightTab==="chat"&&(
            <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden">
              {/* Driver selector */}
              <div className="px-3 py-2 border-b border-slate-100 dark:border-white/5 flex gap-1.5 overflow-x-auto flex-shrink-0">
                <button onClick={()=>setChatDriver(null)} className={`text-[10px] px-2 py-1 rounded-full font-semibold flex-shrink-0 transition-colors ${!chatDriver?"bg-cyan-600 text-white":"bg-slate-100 dark:bg-white/5 text-slate-500"}`}>Broadcast</button>
                {driverList.map(d=>(
                  <button key={d.driver_id} onClick={()=>setChatDriver(d)} className={`text-[10px] px-2 py-1 rounded-full font-semibold flex-shrink-0 transition-colors ${chatDriver?.driver_id===d.driver_id?"bg-cyan-600 text-white":"bg-slate-100 dark:bg-white/5 text-slate-500"}`}>{d.avatar}</button>
                ))}
              </div>
              <div style={{height:"280px"}} className="flex flex-col">
                <DispatcherChat token={token} driver={chatDriver} currentUserId={currentUserId}/>
              </div>
            </div>
          )}

          {/* All drivers tab */}
          {rightTab==="drivers"&&(
            <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-4 space-y-3">
              <p className="text-xs font-bold text-slate-900 dark:text-white">{driverList.length} drivers online</p>
              {driverList.length===0?<div className="text-center py-8 text-[10px] text-slate-400">No drivers broadcasting GPS yet.</div>:
              driverList.map(d=>(
                <div key={d.driver_id} className="flex items-center gap-2.5 p-3 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5">
                  <div className="w-8 h-8 rounded-lg bg-cyan-100 dark:bg-cyan-500/20 flex items-center justify-center font-bold text-cyan-700 dark:text-cyan-300 text-xs flex-shrink-0">{d.avatar}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-800 dark:text-white">{d.driver_name}</p>
                    <p className="text-[10px] font-mono text-slate-400 mt-0.5">{d.lat?.toFixed(4)}°N, {Math.abs(d.lng||0).toFixed(4)}°{d.lng<0?"W":"E"}</p>
                    <p className="text-[10px] text-slate-300 mt-0.5">Updated: {d.timestamp?new Date(d.timestamp).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}):"—"}</p>
                  </div>
                  <button onClick={()=>{setChatDriver(d);setRightTab("chat");}} className="text-[9px] bg-cyan-50 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-500/30 px-2 py-1 rounded-lg font-semibold hover:bg-cyan-100 transition-colors">💬 Chat</button>
                </div>
              ))}
            </div>
          )}

          {/* Alerts tab */}
          {rightTab==="alerts"&&(
            <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-4 space-y-3">
              <p className="text-xs font-bold text-slate-900 dark:text-white">Alerts & Notifications</p>
              {ALERTS.map((a,i)=>(
                <div key={i} className="flex items-start gap-2.5 bg-slate-50 dark:bg-white/5 rounded-xl p-3 border border-slate-100 dark:border-white/5">
                  <div className={`w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0 ${a.type==="warn"?"bg-amber-50 dark:bg-amber-500/10":"bg-blue-50 dark:bg-blue-500/10"}`}>
                    {a.type==="warn"?<AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400"/>:<Info className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400"/>}
                  </div>
                  <div className="flex-1 min-w-0"><p className="text-[10px] font-bold text-slate-800 dark:text-white leading-snug">{a.title}</p><p className="text-[10px] text-slate-400 mt-0.5">{a.sub}</p></div>
                  <span className="text-[9px] text-slate-300 flex-shrink-0 whitespace-nowrap">{a.time}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* GPS Recall Modal */}
      {gpsModal&&createPortal(
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/60 backdrop-blur-sm" onClick={e=>e.target===e.currentTarget&&setGpsModal(false)}>
          <div className="bg-white dark:bg-slate-900 w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col overflow-hidden max-h-[85vh] ring-1 ring-slate-200 dark:ring-white/10">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-white/5 flex-shrink-0">
              <div><h2 className="text-sm font-bold text-slate-900 dark:text-white">📡 GPS Recall</h2><p className="text-[10px] text-slate-400 mt-0.5">{driverList.length} drivers tracked live</p></div>
              <div className="flex items-center gap-2">
                <button className="text-[10px] bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 px-2.5 py-1.5 rounded-lg font-medium hover:bg-slate-100 dark:hover:bg-white/10 transition-colors">↻ Refresh</button>
                <button onClick={()=>setGpsModal(false)} className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"><X className="w-4 h-4"/></button>
              </div>
            </div>
            <div className="overflow-y-auto divide-y divide-slate-50 dark:divide-white/5 flex-1">
              {driverList.length===0?<div className="px-5 py-10 text-center text-xs text-slate-400">No drivers online yet. Waiting for GPS signals…</div>:
              driverList.map(d=>(
                <div key={d.driver_id} className="flex items-center gap-3 px-5 py-3.5 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                  <div className="w-9 h-9 rounded-xl bg-cyan-100 dark:bg-cyan-500/20 flex items-center justify-center font-bold text-cyan-700 dark:text-cyan-300 text-sm flex-shrink-0">{d.avatar}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2"><span className="text-xs font-semibold text-slate-800 dark:text-white">{d.driver_name}</span><span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${d.status==="active"?"bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300":d.status==="on_break"?"bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300":"bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-400"}`}>{d.status?.replace("_"," ")}</span></div>
                    <p className="text-[10px] text-slate-400 font-mono mt-0.5">{d.lat?.toFixed(4)}°N, {Math.abs(d.lng||0).toFixed(4)}°{d.lng<0?"W":"E"}</p>
                    <p className="text-[9px] text-slate-300">Updated: {d.timestamp?new Date(d.timestamp).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}):"—"}</p>
                  </div>
                  <button onClick={()=>{setSelDriver(d);setChatDriver(d);setGpsModal(false);}} className="text-[10px] bg-cyan-50 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-500/30 px-2.5 py-1.5 rounded-lg font-medium hover:bg-cyan-100 dark:hover:bg-cyan-500/20 transition-colors flex-shrink-0">View on Map</button>
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-slate-100 dark:border-white/5 flex-shrink-0">
              <button onClick={()=>setGpsModal(false)} className="w-full border border-slate-200 dark:border-white/10 text-slate-700 dark:text-white py-2.5 rounded-xl text-xs font-medium hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Nav configs ──────────────────────────────────────────────────────────────
const NAV = {
  company:    [{k:"dashboard",l:"Dashboard",I:Home},{k:"loads",l:"My Loads",I:Package,b:"Live"},{k:"drivers",l:"Drivers",I:Users},{k:"analytics",l:"Analytics",I:BarChart2},{k:"settings",l:"Settings",I:Settings}],
  driver:     [{k:"dashboard",l:"Dashboard",I:Home},{k:"loads",l:"Find Loads",I:Map,b:"New"},{k:"training",l:"Training",I:BookOpen},{k:"certs",l:"My Certs",I:Award},{k:"settings",l:"Settings",I:Settings}],
  dispatcher: [{k:"dashboard",l:"Dashboard",I:Home},{k:"tracking",l:"Live Tracking",I:Navigation,b:"Live"},{k:"chat",l:"Driver Chat",I:MessageCircle},{k:"alerts",l:"Alerts",I:Bell,b:"3"},{k:"loads",l:"Load Board",I:Package},{k:"settings",l:"Settings",I:Settings}],
};
const SIDEBAR_COLOR = { company:"bg-indigo-700", driver:"bg-slate-900", dispatcher:"bg-slate-950" };
const BANNER_COLOR  = { company:"bg-indigo-600", driver:"bg-cyan-600", dispatcher:"bg-slate-900" };

// ─── Root Shell ───────────────────────────────────────────────────────────────
export default function UnifiedDashboard() {
  const user        = (() => { try { return JSON.parse(localStorage.getItem("user")); } catch { return null; } })();
  const token       = localStorage.getItem("plu_access") || "";
  const currentUser = user;

  const role      = user?.role || "company";
  const firstName = user?.first_name || user?.name?.split(" ")[0] || (role==="driver"?"Marcus":role==="dispatcher"?"Dispatch":"Team");

  const [activeNav,   setActiveNav]   = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifs,      setNotifs]      = useState(role==="dispatcher"?3:0);
  const [searchQ,     setSearchQ]     = useState("");
  const navItems = NAV[role] || NAV.company;

  const AVATAR_COLOR = { company:"bg-indigo-600", driver:"bg-cyan-600", dispatcher:"bg-slate-700" };
  const CTA_LABEL    = { company:"Post Load", driver:"Find Loads", dispatcher:"GPS Recall" };

  const h = new Date().getHours();
  const greeting = h<12?"Good morning":h<17?"Good afternoon":"Good evening";

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-950 overflow-hidden" style={{fontFamily:"Inter,system-ui,sans-serif"}}>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm" onClick={()=>setSidebarOpen(false)}/>}

      {/* ── SIDEBAR ── */}
      <aside className={`fixed lg:relative z-50 lg:z-auto top-0 left-0 h-full w-56 flex flex-col transition-transform duration-300 ${sidebarOpen?"translate-x-0":"-translate-x-full lg:translate-x-0"} ${SIDEBAR_COLOR[role]}`}>
        {/* Logo */}
        <div className="flex items-center gap-2.5 px-4 py-4 border-b border-white/10 flex-shrink-0">
          <div className="w-8 h-8 bg-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0"><Truck className="w-4.5 h-4.5 text-white"/></div>
          <div className="flex-1 min-w-0"><p className="text-xs font-bold text-white leading-tight">PilotLinkUSA</p><p className="text-[9px] text-white/40 uppercase tracking-widest mt-0.5">{role==="company"?"Company Portal":role==="driver"?"Driver Portal":"Dispatcher"}</p></div>
          <button onClick={()=>setSidebarOpen(false)} className="lg:hidden text-white/40 hover:text-white"><X className="w-4 h-4"/></button>
        </div>

        {/* User */}
        <div className="mx-3 my-2 rounded-xl p-2.5 flex items-center gap-2 flex-shrink-0" style={{background:"rgba(255,255,255,0.08)"}}>
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0 ${AVATAR_COLOR[role]}`}>{firstName.slice(0,1).toUpperCase()}{role==="company"?"O":role==="driver"?"R":"C"}</div>
          <div className="flex-1 min-w-0"><p className="text-[11px] font-semibold text-white truncate">{firstName}</p><p className="text-[9px] text-white/40 capitalize">{role==="company"?"Company Account":role==="driver"?"Pilot Car Driver":"Dispatcher"}</p></div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-1 overflow-y-auto space-y-0.5">
          <p className="px-2 py-1 text-[9px] font-bold uppercase tracking-widest text-white/30">Main Menu</p>
          {navItems.map(({k,l,I,b})=>(
            <button key={k} onClick={()=>{setActiveNav(k);setSidebarOpen(false);}} className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-[11px] font-medium transition-all ${activeNav===k?"bg-white/15 text-white":"text-white/50 hover:bg-white/8 hover:text-white"}`}>
              <I className="w-4 h-4 flex-shrink-0"/>
              <span className="truncate">{l}</span>
              {b&&<span className={`ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${activeNav===k?"bg-white/20 text-white":"bg-white/10 text-white/60"}`}>{b}</span>}
            </button>
          ))}
          <div className="border-t border-white/10 my-2"/>
          <button onClick={()=>{localStorage.removeItem("user");localStorage.removeItem("plu_access");window.location.href="/login";}} className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-[11px] font-medium text-red-300 hover:bg-red-500/10 transition-colors">
            <LogOut className="w-4 h-4 flex-shrink-0"/> Sign Out
          </button>
        </nav>

        {/* Help */}
        <div className="p-3 flex-shrink-0">
          <div className="rounded-xl p-3 text-center" style={{background:"rgba(255,255,255,0.07)"}}>
            <Zap className="w-5 h-5 text-cyan-400 mx-auto mb-1.5"/>
            <p className="text-[10px] font-bold text-white mb-0.5">Need Help?</p>
            <p className="text-[9px] text-white/40 mb-2">Our team is 24/7</p>
            <button className="w-full bg-cyan-600 hover:bg-cyan-500 text-white text-[10px] font-bold py-1.5 rounded-lg transition-colors">Contact Support</button>
          </div>
        </div>
      </aside>

      {/* ── MAIN ── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <header className="h-13 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-white/10 flex items-center px-3 sm:px-5 gap-3 flex-shrink-0" style={{height:"52px"}}>
          <button onClick={()=>setSidebarOpen(true)} className="lg:hidden w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5"><Menu className="w-5 h-5"/></button>
          <div className="hidden lg:block"><p className="text-sm font-bold text-slate-900 dark:text-white leading-tight">{navItems.find(n=>n.k===activeNav)?.l||"Dashboard"}</p><p className="text-[10px] text-slate-400">{greeting}, {firstName} 👋</p></div>
          <div className="lg:hidden flex-1 min-w-0"><p className="text-xs font-bold text-slate-900 dark:text-white truncate">{greeting}, {firstName}</p></div>
          <div className="flex-1 max-w-xs mx-3 hidden sm:flex items-center gap-2 bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-3 py-2">
            <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0"/>
            <input value={searchQ} onChange={e=>setSearchQ(e.target.value)} placeholder="Search loads, drivers…" className="flex-1 bg-transparent text-xs text-slate-700 dark:text-white placeholder-slate-400 outline-none"/>
            {searchQ&&<button onClick={()=>setSearchQ("")}><X className="w-3 h-3 text-slate-400"/></button>}
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            {role==="dispatcher"&&(
              <div className={`hidden sm:flex items-center gap-1.5 text-[10px] font-medium px-2 py-1 rounded-full ${true?"bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300":"bg-red-100 text-red-700"}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"/>GPS Live
              </div>
            )}
            <button onClick={()=>setNotifs(0)} className="relative w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors">
              <Bell className="w-4 h-4"/>
              {notifs>0&&<span className="absolute top-1.5 right-1.5 w-3.5 h-3.5 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">{notifs}</span>}
            </button>
            <button onClick={()=>window.location.reload()} className="hidden sm:flex w-8 h-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"><RefreshCw className="w-3.5 h-3.5"/></button>
            <button className="hidden sm:flex items-center gap-1.5 text-white text-[10px] font-bold px-3 py-1.5 rounded-xl transition-colors" style={{background:SIDEBAR_COLOR[role].replace("bg-","").includes("indigo")?"#4f46e5":role==="driver"?"#0ea5e9":"#1a2744"}}>
              {role==="dispatcher"?<Satellite className="w-3.5 h-3.5"/>:role==="driver"?<Map className="w-3.5 h-3.5"/>:<Plus className="w-3.5 h-3.5"/>}
              {CTA_LABEL[role]}
            </button>
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 ${AVATAR_COLOR[role]}`}>{firstName.slice(0,1).toUpperCase()}</div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-3 sm:px-5 py-5">
            {activeNav!=="dashboard" ? (
              <div className="flex flex-col items-center justify-center h-96 text-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-white/5 flex items-center justify-center">
                  {(() => { const Item = navItems.find(n=>n.k===activeNav); return Item ? <Item.I className="w-7 h-7 text-slate-400"/> : null; })()}
                </div>
                <div><p className="text-sm font-bold text-slate-900 dark:text-white mb-1">{navItems.find(n=>n.k===activeNav)?.l}</p><p className="text-xs text-slate-400 max-w-xs leading-relaxed">This section connects to your existing React Router routes and renders in production.</p></div>
                <Btn variant="outline" size="sm" onClick={()=>setActiveNav("dashboard")}>← Back to Dashboard</Btn>
              </div>
            ) : role==="company"     ? <CompanyView    token={token}/>
              : role==="driver"      ? <DriverView     token={token}/>
              : role==="dispatcher"  ? <DispatcherView token={token} currentUserId={currentUser?.id}/>
              : null}
          </div>
        </main>

        {/* Mobile bottom nav */}
        <nav className="lg:hidden flex-shrink-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-white/10 flex" style={{paddingBottom:"env(safe-area-inset-bottom)"}}>
          {navItems.slice(0,5).map(({k,l,I})=>(
            <button key={k} onClick={()=>setActiveNav(k)} className={`flex-1 flex flex-col items-center justify-center py-2 gap-0.5 transition-colors ${activeNav===k?"text-cyan-600 dark:text-cyan-400":"text-slate-400 dark:text-slate-500"}`}>
              <I className="w-5 h-5"/>
              <span className="text-[9px] font-medium">{l}</span>
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
}
